import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import fetchWithCredentials from "apiClient";
import { Worker } from "types/Worker";

interface WorkersContextType {
  workers: Worker[];
  otherCondoWorkers: Worker[];
  loading: boolean;
  error: string | null;
  refresh: (condoId: number | null) => Promise<void>;
  workersCount: number;
  selectedContactId: number | null;
  setSelectedContactId: (contactId: number | null) => void;
  markContactAsRead: (contactId: number) => void;
  updateLastReadAt: (contactId: number, lastReadAt: string) => void;
}

const WorkersContext = createContext<WorkersContextType | undefined>(undefined);

export function WorkersProvider({ children }: { children: ReactNode }) {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [otherCondoWorkers, setOtherCondoWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null,
  );

  const fetchWorkers = useCallback(async (condoId: number | null) => {
    if (!condoId) {
      setWorkers([]);
      setOtherCondoWorkers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithCredentials(
        `/manager-api/workers/?condo=${condoId}`,
      );
      setWorkers(Array.isArray(data?.condoWorkers) ? data.condoWorkers : []);
      setOtherCondoWorkers(
        Array.isArray(data?.otherCondoWorkers) ? data.otherCondoWorkers : [],
      );
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching workers");
      setWorkers([]);
      setOtherCondoWorkers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(
    async (condoId: number | null) => {
      await fetchWorkers(condoId);
    },
    [fetchWorkers],
  );

  const markContactAsRead = useCallback((contactId: number) => {
    const mark = (list: Worker[]) =>
      list.map((worker) =>
        worker.contact.id === contactId
          ? { ...worker, hasUnread: false }
          : worker,
      );
    setWorkers(mark);
    setOtherCondoWorkers(mark);
  }, []);

  const updateLastReadAt = useCallback(
    (contactId: number, lastReadAt: string) => {
      const update = (list: Worker[]) =>
        list.map((worker) =>
          worker.contact.id === contactId ? { ...worker, lastReadAt } : worker,
        );
      setWorkers(update);
      setOtherCondoWorkers(update);
    },
    [],
  );

  const workersCount = useMemo(
    () => (Array.isArray(workers) ? workers.length : 0),
    [workers],
  );

  return (
    <WorkersContext.Provider
      value={{
        workers,
        otherCondoWorkers,
        loading,
        error,
        refresh,
        workersCount,
        selectedContactId,
        setSelectedContactId,
        markContactAsRead,
        updateLastReadAt,
      }}
    >
      {children}
    </WorkersContext.Provider>
  );
}

export function useWorkers() {
  const context = useContext(WorkersContext);
  if (!context) {
    throw new Error("useWorkers must be used within a WorkersProvider");
  }
  return context;
}
